/**
 * MissingPositionBanner
 * 役職が未登録のまま乗船中の乗組員がいるとき、陸上管理サイトの全ページ上部に大きく警告を出す。
 * 船員アプリの自己乗船は役職を持たないため、船長などが自己乗船すると役職ベースの権限
 * （書類作成・勤怠閲覧など）が効かなくなる。ここから直接役職を登録できるほか、
 * 配乗シミュレーションへのリンクも置く。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';
import * as api from '../services/api';

interface MissingRow {
  assignment_id: string; boarded_at: string | null; user_id: string; vessel_id: string;
  user_name: string; employee_code: string | null; rank_position: string | null; vessel_name: string;
}

const POLL_MS = 60_000;

export default function MissingPositionBanner({ canEdit }: { canEdit: boolean }) {
  const location = useLocation();
  const [rows, setRows] = useState<MissingRow[]>([]);
  const [positions, setPositions] = useState<string[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ data: MissingRow[] }>('/vessels/assignments/missing-position');
      const list = r?.data ?? [];
      setRows(list);
      // 初期値は本人の辞令上の役職（ユーザー管理の役職欄）を提案する
      setDraft(prev => {
        const next = { ...prev };
        for (const x of list) if (next[x.assignment_id] === undefined) next[x.assignment_id] = x.rank_position ?? '';
        return next;
      });
    } catch { /* 権限外・オフラインは黙って非表示 */ }
  }, []);

  useEffect(() => { load(); }, [load, location.pathname]);
  useEffect(() => { const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    if (rows.length === 0 || positions.length > 0) return;
    api.get<{ data: Array<{ name: string; is_active?: number }> }>('/position-masters')
      .then(r => setPositions((r?.data ?? []).filter(x => x.is_active !== 0).map(x => x.name)))
      .catch(() => { /* マスタ未整備なら手入力なし */ });
  }, [rows.length, positions.length]);

  const save = async (row: MissingRow) => {
    const position = (draft[row.assignment_id] ?? '').trim();
    if (!position) { alert('役職を選択してください'); return; }
    setSaving(row.assignment_id);
    try {
      await api.patch(`/vessels/${row.vessel_id}/crew/${row.user_id}/position`, { position, assignment_id: row.assignment_id });
      await load();
    } catch (e: any) {
      alert(e?.message ?? '役職の登録に失敗しました');
    }
    setSaving(null);
  };

  if (rows.length === 0) return null;

  return (
    <div role="alert" style={{
      background: 'linear-gradient(90deg, #b91c1c 0%, #dc2626 100%)', color: '#fff',
      padding: collapsed ? '8px 18px' : '12px 18px', boxShadow: '0 2px 8px rgba(185,28,28,0.35)', flexShrink: 0, zIndex: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <AlertTriangle size={22} strokeWidth={2.5} />
        <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: 0.2 }}>
          役職が登録されていない乗組員が {rows.length} 名います
        </div>
        <div style={{ fontSize: 12.5, opacity: 0.95 }}>
          船員アプリからの自己乗船などで役職が入っていません。役職が無いと船長の書類作成などの権限が効きません。
        </div>
        <Link to="/crew-sim" style={{
          marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#b91c1c',
          fontWeight: 800, fontSize: 13, padding: '7px 14px', borderRadius: 8, textDecoration: 'none', whiteSpace: 'nowrap',
        }}>
          <ExternalLink size={14} />役職を登録するページへ
        </Link>
        <button onClick={() => setCollapsed(c => !c)} title={collapsed ? '一覧を表示' : '一覧を畳む'}
          style={{ background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
          {collapsed ? '一覧を表示' : <X size={14} />}
        </button>
      </div>
      {!collapsed && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          {rows.map(r => (
            <div key={r.assignment_id} style={{
              display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.14)', borderRadius: 10,
              padding: '6px 10px', fontSize: 13,
            }}>
              <span style={{ fontWeight: 800 }}>{r.user_name}</span>
              <span style={{ opacity: 0.9 }}>{r.vessel_name}</span>
              {r.boarded_at && <span style={{ opacity: 0.8, fontSize: 11.5 }}>乗船 {String(r.boarded_at).slice(0, 10)}</span>}
              {canEdit ? (
                <>
                  <select value={draft[r.assignment_id] ?? ''} onChange={e => setDraft(d => ({ ...d, [r.assignment_id]: e.target.value }))}
                    style={{ padding: '4px 8px', borderRadius: 6, border: 'none', fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>
                    <option value="">役職を選択</option>
                    {positions.map(p => <option key={p} value={p}>{p}</option>)}
                    {r.rank_position && !positions.includes(r.rank_position) && <option value={r.rank_position}>{r.rank_position}</option>}
                  </select>
                  <button onClick={() => save(r)} disabled={saving === r.assignment_id}
                    style={{ background: '#fff', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
                    {saving === r.assignment_id ? '登録中…' : '登録'}
                  </button>
                </>
              ) : (
                <span style={{ fontSize: 11.5, opacity: 0.85 }}>（管理者に役職の登録を依頼してください）</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
